import { useMemo } from 'react';
import {
  AllergyBasicScratchMhoSuggestion,
  BasicScratchMhoSuggestion,
  BiChoiceScratchMhoSuggestion,
  ClearScratchMhoSuggestion,
  DrawerScratchMhoSuggestion,
  DrawerSuggestionDataPayload,
  NumericScratchMhoSuggestion,
  ScoreScratchMhoSuggestion,
  ScratchMhoSuggestionData,
  ScratchSuggestion,
  ScratchSuggestionPredicate,
  YesNoChoiceScratchMhoSuggestion
} from './scratchMhoSuggestion.model';
import {
  AllergyService,
  MedicalHoItemCategory,
  MedicalHoService,
  MedicalScoreName,
  SurgicalEvent
} from '@/modules/surgicalEvent';
import { Gender } from '@ianesthesie-kit/services';
import {
  MhoSuggestion as ConfigSuggestion,
  rulesMatchPatient,
  useGetMhoSuggestionsQuery
} from '@/modules/mhoSuggestions';
import type {
  MhoSuggestionsPerCategory,
  ScratchMhoSuggestionCategory
} from './scratchMhoSuggestion.service';

/**
 * Composite key matching a config row to a static handler.
 * Composite (qcode, identifier) — many static handlers share a qcode (e.g.
 * multiple BiChoices under PreviousPregnanciesHO) and code alone (e.g.
 * 'NONE', 'YES') is not category-unique. For SNOMED-only templates the
 * identifier falls back to the SNOMED code so that no qcode/code pair needs
 * to be invented for them.
 */
const handlerKey = (
  qcode: string | undefined,
  identifier: string | undefined
) => `${qcode ?? ''}::${identifier ?? ''}`;

const configHandlerKey = (cfg: ConfigSuggestion) =>
  handlerKey(cfg.qcode, cfg.code ?? cfg.snomedCode);

type HandlerFactory = (cfg: ConfigSuggestion) => ScratchSuggestion;

const patientPredicate =
  (cfg: ConfigSuggestion): ScratchSuggestionPredicate =>
  (se) =>
    rulesMatchPatient(cfg.rules, se.patient);

export class ScratchSuggestionHelper {
  static readonly DrawerSuggestionsByQcode: Record<
    string,
    DrawerScratchMhoSuggestion
  > = {
    CardiologyHO: new DrawerScratchMhoSuggestion(
      'CardiologyHO',
      'HeartAttackHO',
      this.buildHeartAttackDrawerPayload()
    ),
    Smoker: new DrawerScratchMhoSuggestion(
      'Smoker',
      'Smoker',
      this.buildSmokerDrawerPayload()
    ),
    Asthma: new DrawerScratchMhoSuggestion(
      'Asthma',
      undefined,
      this.buildAsthmaDrawerPayload()
    ),
    StrokeHO: new DrawerScratchMhoSuggestion(
      'StrokeHO',
      'YES',
      this.buildStrokeHoDrawerPayload()
    ),
    HOVenousThrombosis: new DrawerScratchMhoSuggestion(
      'HOVenousThrombosis',
      'YES',
      this.buildThrombosisHoDrawerPayload()
    )
  };

  /** Matches questionnaire placement for facts whose catalog category differs. */
  static getQuestionnaireCategory(
    { qcode, code }: ScratchMhoSuggestionData,
    fallback: MedicalHoItemCategory | undefined
  ): MedicalHoItemCategory | undefined {
    if (qcode === 'HOChronicPainAlgodystrophy') {
      return MedicalHoItemCategory.SURGICAL;
    }
    if (qcode === 'Gravidity' || qcode === 'Parity') {
      return MedicalHoItemCategory.GYN;
    }
    if (qcode === 'PreviousPregnanciesHO') {
      return code === 'PreviousPregnanciesHO.EmergencyCsection' ||
        code === 'PreviousPregnanciesHO.EmergencyCsection.NO'
        ? MedicalHoItemCategory.SURGICAL
        : MedicalHoItemCategory.GYN;
    }
    if (qcode === 'MetabolicQuestions') {
      if (code === 'HepatitisHO') {
        return MedicalHoItemCategory.GENERAL;
      }
      if (code === 'LiverCirrhosis') {
        return MedicalHoItemCategory.GI;
      }
    }
    return fallback;
  }

  static buildAllergySuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    const configured = (config ?? [])
      .filter((s) => s.category === 'ALLERGIES')
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex);

    return [
      new ClearScratchMhoSuggestion(
        'clear_allergies',
        [{ code: AllergyService.SC_CLEARED_CODE }],
        'doctor-dashboard.details.allergies.none',
        false
      ) as ScratchSuggestion,
      ...configured.map(
        (cfg) =>
          new AllergyBasicScratchMhoSuggestion(
            cfg.code ?? cfg.snomedCode ?? cfg.freeTextLabel!,
            cfg.snomedCode,
            undefined,
            cfg.code ? undefined : cfg.freeTextLabel,
            patientPredicate(cfg),
            cfg.defaultEmphasis
          )
      )
    ];
  }

  static buildSurgicalSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    const configured = (config ?? [])
      .filter((s) => s.category === MedicalHoItemCategory.SURGICAL)
      .slice()
      .sort((a, b) => a.orderIndex - b.orderIndex);

    return [
      new ClearScratchMhoSuggestion(
        'clear_surgical',
        [{ qcode: 'PreviousProcedures', code: 'NONE' }],
        undefined,
        false
      ) as ScratchSuggestion,
      ...configured.map((s) => {
        const codeKey = s.code ?? s.snomedCode ?? s.freeTextLabel!;
        const translationOverride = s.code
          ? `medical_synthesis.TRIAGE_${s.code}`
          : s.freeTextLabel;
        return new BasicScratchMhoSuggestion(
          codeKey,
          s.qcode ?? (s.code ? 'PreviousProcedures' : undefined),
          s.snomedCode,
          undefined,
          translationOverride,
          patientPredicate(s),
          s.defaultEmphasis
        );
      })
    ];
  }

  static buildAnesthesiaSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.ANESTHESIA,
      config,
      ANESTHESIA_HANDLERS,
      [
        new ClearScratchMhoSuggestion(
          'clear_anesthesia',
          [{ qcode: 'AnesthesiaInThePast', code: 'NO' }],
          undefined,
          false
        )
      ]
    );
  }

  static buildCardioSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.CARDIO_VASCULAR,
      config,
      CARDIO_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_cardio', [
          { qcode: 'TwoFlightsStairs', code: 'YES' },
          { qcode: 'CardiacRhythm', code: 'Arrhythmia.NO' },
          { qcode: 'CardiacRhythm', code: 'Pacemaker.NO' },
          { qcode: 'CardiologyHO', code: 'ChestPainHO.NO' },
          { qcode: 'CardiologyHO', code: 'HeartAttackHO.NO' },
          { qcode: 'CardiologyHO', code: 'HeartFailure.NO' },
          { qcode: 'CardiologyHO', code: 'HighBloodPressure.NO' },
          { qcode: 'HOVenousThrombosis', code: 'NO' },
          {
            qcode: MedicalHoService.getQcodeByScoreName(MedicalScoreName.LEE),
            isScore: true,
            code: ''
          },
          {
            qcode: MedicalHoService.getQcodeByScoreName(
              MedicalScoreName.CARDIAC_RISK_FACTOR
            ),
            isScore: true,
            code: ''
          }
        ])
      ]
    );
  }

  static buildPulmonarySuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.PULMONARY,
      config,
      PULMONARY_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_pulmo', [
          { qcode: 'Smoker', code: 'NotSmoker' },
          { qcode: 'Asthma', code: 'NoAsthma' },
          { qcode: 'SleepApnea', code: 'NO' },
          { qcode: 'ChronicBronchitis', code: 'NO' }
        ])
      ]
    );
  }

  static buildNeuroSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    const ageOver45 = (se: SurgicalEvent) => se.patient.age > 45;
    return buildFromConfig(
      MedicalHoItemCategory.NEURO,
      config,
      NEURO_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_neuro', [
          { qcode: 'StrokeHO', code: 'NO' },
          { qcode: 'EpilepsyHO', code: 'NO' },
          { qcode: 'ConsciousnessLossHO', code: 'NO' },
          { qcode: 'DepressionHO', code: 'NO' },
          { qcode: 'MigraineHO', code: 'NO' },
          { qcode: 'AlzheimerHO', code: 'NO', predicate: ageOver45 },
          { qcode: 'ParkinsonHO', code: 'NO', predicate: ageOver45 }
        ])
      ]
    );
  }

  static buildGiSuggestions(config?: ConfigSuggestion[]): ScratchSuggestion[] {
    return buildFromConfig(MedicalHoItemCategory.GI, config, GI_HANDLERS, [
      new ClearScratchMhoSuggestion('clear_gi', [
        { qcode: 'GastricReflux', code: 'NONE' },
        { qcode: 'StomachProblemHO', code: 'StomachProblemHO.GastricUlcer.NO' },
        { qcode: 'StomachProblemHO', code: 'StomachProblemHO.HiatalHernia.NO' }
      ])
    ]);
  }

  static buildRenalSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    const isMale = (se: SurgicalEvent) => se.patient.gender === Gender.MALE;
    return buildFromConfig(
      MedicalHoItemCategory.RENAL,
      config,
      RENAL_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_renal', [
          {
            qcode: 'ChronicKidneyFailureHO',
            code: 'ChronicKidneyFailureHO.No'
          },
          { qcode: 'ProstateProblemsHO', code: 'NO', predicate: isMale }
        ])
      ]
    );
  }

  static buildEndocrineSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.ENDOCRINE,
      config,
      ENDOCRINE_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_endocrine', [
          { qcode: 'MetabolicQuestions', code: 'NONE' },
          { qcode: 'AlcoholConsumptionHO', code: 'AlcoholConsumptionHO.Never' },
          { qcode: 'MetabolicQuestions', code: 'Diabetes.NO' }
        ])
      ]
    );
  }

  static buildHematoSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.HEMATO,
      config,
      HEMATO_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_hemato', [
          { qcode: 'HOBloodTransfusion', code: 'NO' },
          {
            qcode: MedicalHoService.getQcodeByScoreName(
              MedicalScoreName.HEMSTOP
            ),
            isScore: true,
            code: ''
          }
        ])
      ]
    );
  }

  static buildEntSuggestions(config?: ConfigSuggestion[]): ScratchSuggestion[] {
    return buildFromConfig(MedicalHoItemCategory.ENT, config, ENT_HANDLERS, [
      new ClearScratchMhoSuggestion('clear_ent', [
        { qcode: 'Braces', code: 'NoBraces' },
        { qcode: 'FragileTeeth', code: 'NO' },
        { qcode: 'JawJointsProblem', code: 'NO' }
      ])
    ]);
  }

  static buildFamilySuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.FAMILY,
      config,
      FAMILY_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_family', [
          { qcode: 'CoagulationRiskFamily', code: 'NO' },
          { qcode: 'MalignantHyperthermiaFamily', code: 'NO' },
          { qcode: 'VascularRiskFamily', code: 'NO' }
        ])
      ]
    );
  }

  static buildObstetricsSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.OBSTETRIC,
      config,
      OBSTETRIC_HANDLERS,
      [
        new ClearScratchMhoSuggestion(
          'clear_obs_nullipara',
          [
            { qcode: 'Gravidity', code: '0' },
            { qcode: 'Parity', code: '0' }
          ],
          'doctor-dashboard.scratch.clear.nullipara'
        ),
        new ClearScratchMhoSuggestion(
          'clear_obs_primipara',
          [
            { qcode: 'Gravidity', code: '1' },
            { qcode: 'Parity', code: '1' },
            {
              qcode: 'PreviousPregnanciesHO',
              code: 'PreviousPregnanciesHO.PostpartumHemorrhage.NO'
            },
            {
              qcode: 'PreviousProcedures',
              code: 'SurgeryName.Childbirth',
              category: MedicalHoItemCategory.SURGICAL
            }
          ],
          'doctor-dashboard.scratch.clear.primipara'
        )
      ]
    );
  }

  static buildGeneralSuggestions(
    config?: ConfigSuggestion[]
  ): ScratchSuggestion[] {
    return buildFromConfig(
      MedicalHoItemCategory.GENERAL,
      config,
      GENERAL_HANDLERS,
      [
        new ClearScratchMhoSuggestion('clear_general', [
          {
            qcode: 'CurrentInfections',
            code: 'CurrentInfections.HepatitisB.NO'
          },
          {
            qcode: 'CurrentInfections',
            code: 'CurrentInfections.HepatitisC.NO'
          },
          { qcode: 'CurrentInfections', code: 'CurrentInfections.HIV.NO' },
          { qcode: 'MyastheniaHO', code: 'NO' },
          { qcode: 'OtherToxics', code: 'NONE' }
        ])
      ]
    );
  }

  static buildHeartAttackDrawerPayload(): DrawerSuggestionDataPayload[] {
    return [
      {
        type: 'yesNo',
        qcode: 'HeartAttackHODetails',
        choices: [
          'HeartAttackHODetails.CoronaryBypass',
          'HeartAttackHODetails.CoronaryBypass.NO'
        ]
      },
      {
        type: 'yesNo',
        qcode: 'HeartAttackHODetails',
        choices: ['HeartAttackHODetails.Stent', 'HeartAttackHODetails.Stent.NO']
      },
      { type: 'date', qcode: 'HeartAttackHODate' }
    ];
  }

  static buildAsthmaDrawerPayload(): DrawerSuggestionDataPayload[] {
    return [
      {
        type: 'enum',
        qcode: 'Asthma',
        choices: [
          'Asthma.ChildhoodAsthma',
          'Asthma.ControlledAsthma',
          'Asthma.UnControlledAsthma'
        ]
      },
      {
        type: 'yesNo',
        qcode: 'AsthmaHOIcu'
      },
      {
        type: 'enum',
        multiple: true,
        qcode: 'AsthmaDetailsSeverity',
        choices: [
          'AsthmaDetailsSeverity.1OrMoreNightEpisodePerWeek',
          'AsthmaDetailsSeverity.3OrMoreDailyEpisodesPerWeek',
          'AsthmaDetailsSeverity.LimitedForActivities',
          'AsthmaDetailsSeverity.OneMajorEpisodeOrMoreLastYear',
          'AsthmaDetailsSeverity.RescueMed3OrMorePerWeek'
        ]
      }
    ];
  }

  static buildSmokerDrawerPayload(): DrawerSuggestionDataPayload[] {
    const smokerWantsToStop: DrawerSuggestionDataPayload = {
      type: 'enum',
      qcode: 'SmokerWantToStop',
      choices: [
        'SmokerDoesNotWantToStop',
        'SmokerWantToStop',
        'SmokerWantToStop.YesNeedsHelp'
      ]
    };
    const wantsToStopSmoking = (
      payload: Record<string, string | number | string[]>
    ) => {
      const uniquified =
        DrawerScratchMhoSuggestion.uniquifyQcode(smokerWantsToStop)!;
      return (
        typeof payload[uniquified] === 'string' &&
        (payload[uniquified] as string).startsWith('SmokerWantToStop')
      );
    };

    return [
      {
        type: 'enum',
        qcode: 'SmokerIntensity',
        choices: [
          'SmokerIntensity.IntenseSmoker',
          'SmokerIntensity.ModerateSmoker',
          'SmokerIntensity.LightSmoker',
          'SmokerIntensity.OccasionalSmoker'
        ]
      },
      smokerWantsToStop,
      {
        type: 'enum',
        predicate: wantsToStopSmoking,
        qcode: 'SmokerFirstCigDelay',
        choices: [
          'SmokerFirstCigDelay.LessThanFiveMin',
          'SmokerFirstCigDelay.FivetoThirtyMin',
          'SmokerFirstCigDelay.ThirtytoSixtyMin',
          'SmokerFirstCigDelay.MoreThanSixtyMin'
        ]
      },
      {
        type: 'enum',
        predicate: wantsToStopSmoking,
        qcode: 'SmokerCigPerDay',
        choices: [
          'SmokerCigPerDay.LessThanTen',
          'SmokerCigPerDay.TenToTwenty',
          'SmokerCigPerDay.TwentyToThirty',
          'SmokerCigPerDay.MoreThanThirty'
        ]
      }
    ];
  }

  static buildStrokeHoDrawerPayload(): DrawerSuggestionDataPayload[] {
    return [
      {
        type: 'enum',
        multiple: true,
        qcode: 'StrokeHODetails',
        choices: [
          'StrokeHODetails.TransientIschemicAttack',
          'StrokeHODetails.WithoutSequelae',
          'StrokeHODetails.WithSequelae'
        ]
      },
      {
        type: 'range',
        qcode: 'StrokeHODetailsFrequency',
        choices: [1, 2, 3, 4, 5]
      },
      {
        type: 'date',
        qcode: 'StrokeHODetailsLastEpisodeDate'
      },
      {
        type: 'enum',
        multiple: true,
        qcode: 'StrokeHODetailsPlus',
        choices: [
          'StrokeHODetailsPlus.ArterialVenousMalformation',
          'StrokeHODetailsPlus.Cancer',
          'StrokeHODetailsPlus.Cardiac',
          'StrokeHODetailsPlus.CoagulationDisease',
          'StrokeHODetailsPlus.Infection',
          'StrokeHODetailsPlus.Undetermined'
        ]
      }
    ];
  }

  static buildThrombosisHoDrawerPayload(): DrawerSuggestionDataPayload[] {
    return [
      {
        type: 'enum',
        multiple: true,
        qcode: 'HOVenousThrombosisDetails',
        choices: [
          'HOVenousThrombosisDetails.HOPulmonaryEmbolism',
          'HOVenousThrombosisDetails.HOThrombosis'
        ]
      },
      {
        type: 'range',
        qcode: 'HOVenousThrombosisDetailsFrequency',
        choices: [1, 2, 3, 4, 5]
      },
      {
        type: 'date',
        qcode: 'HOVenousThrombosisDetailsLastEpisodeDate'
      },
      {
        type: 'enum',
        multiple: true,
        qcode: 'HOVenousThrombosisDetailsPlus',
        choices: [
          'HOVenousThrombosisDetailsPlus.Arrhythmia',
          'HOVenousThrombosisDetailsPlus.Cancer',
          'HOVenousThrombosisDetailsPlus.CoagulationDisease',
          'HOVenousThrombosisDetailsPlus.Immobilization',
          'HOVenousThrombosisDetailsPlus.Infection',
          'HOVenousThrombosisDetailsPlus.Undetermined'
        ]
      }
    ];
  }
}

function customBasicChip(cfg: ConfigSuggestion): ScratchSuggestion {
  const codeKey = cfg.code ?? cfg.snomedCode ?? cfg.freeTextLabel!;
  const translationOverride = cfg.code
    ? `medical_synthesis.TRIAGE_${cfg.code}`
    : cfg.freeTextLabel;
  return new BasicScratchMhoSuggestion(
    codeKey,
    cfg.qcode,
    cfg.snomedCode,
    undefined,
    translationOverride,
    patientPredicate(cfg),
    cfg.defaultEmphasis
  );
}

function buildFromConfig(
  category: ScratchMhoSuggestionCategory,
  config: ConfigSuggestion[] | undefined,
  handlers: Record<string, HandlerFactory>,
  clears: ScratchSuggestion[]
): ScratchSuggestion[] {
  const configured = (config ?? [])
    .filter((s) => s.category === category)
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const built = configured
    .map((cfg) => {
      if (cfg.isCustom) {
        return customBasicChip(cfg);
      }
      const factory = handlers[configHandlerKey(cfg)];
      return factory ? factory(cfg) : customBasicChip(cfg);
    })
    .filter((s): s is ScratchSuggestion => !!s);

  return [...clears, ...built];
}

// ---------- Per-category handler registries ----------
// Each entry's key is `${qcode}::${code}` from the DB row. Custom rows
// (is_custom=true) bypass this map and render as Basic chips via
// customFactory. Rows whose code is not in the map (a silent
// rolling-deploy gap, per the doc) are skipped.

const basic =
  (
    code: string,
    qcode?: string,
    snomedCode?: string,
    icd10Code?: string,
    translationOverride?: string
  ): HandlerFactory =>
  (cfg) =>
    new BasicScratchMhoSuggestion(
      code,
      qcode,
      snomedCode,
      icd10Code,
      translationOverride,
      patientPredicate(cfg),
      cfg.defaultEmphasis
    );

const yesNo =
  (qcode: string): HandlerFactory =>
  (cfg) =>
    new YesNoChoiceScratchMhoSuggestion(
      qcode,
      undefined,
      patientPredicate(cfg)
    );

const biChoice =
  (qcode: string, leftCode: string, rightCode: string): HandlerFactory =>
  (cfg) =>
    new BiChoiceScratchMhoSuggestion(
      qcode,
      leftCode,
      rightCode,
      undefined,
      patientPredicate(cfg)
    );

const DRAWER_PAYLOAD_FACTORIES: Record<
  string,
  { code?: string; payload: () => DrawerSuggestionDataPayload[] }
> = {
  CardiologyHO: {
    code: 'HeartAttackHO',
    payload: () => ScratchSuggestionHelper.buildHeartAttackDrawerPayload()
  },
  Smoker: {
    code: 'Smoker',
    payload: () => ScratchSuggestionHelper.buildSmokerDrawerPayload()
  },
  Asthma: {
    code: undefined,
    payload: () => ScratchSuggestionHelper.buildAsthmaDrawerPayload()
  },
  StrokeHO: {
    code: 'YES',
    payload: () => ScratchSuggestionHelper.buildStrokeHoDrawerPayload()
  },
  HOVenousThrombosis: {
    code: 'YES',
    payload: () => ScratchSuggestionHelper.buildThrombosisHoDrawerPayload()
  }
};

const drawer =
  (qcode: string): HandlerFactory =>
  (cfg) => {
    const spec = DRAWER_PAYLOAD_FACTORIES[qcode];
    if (!spec) {
      throw new Error(`Unknown drawer qcode: ${qcode}`);
    }
    return new DrawerScratchMhoSuggestion(
      qcode,
      spec.code,
      spec.payload(),
      true,
      undefined,
      patientPredicate(cfg)
    );
  };

const score =
  (s: MedicalScoreName): HandlerFactory =>
  (cfg) =>
    new ScoreScratchMhoSuggestion(s, patientPredicate(cfg));

const numericUniq =
  (qcode: string, value: string): HandlerFactory =>
  (cfg) =>
    new NumericScratchMhoSuggestion(
      value,
      qcode,
      undefined,
      undefined,
      undefined,
      patientPredicate(cfg)
    );

const ANESTHESIA_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('AnesthesiaComplications', 'NONE')]: basic(
    'NONE',
    'AnesthesiaComplications'
  ),
  [handlerKey(
    'AnesthesiaComplications',
    'AnesthesiaComplications.PostopNauseaVomiting'
  )]: basic(
    'AnesthesiaComplications.PostopNauseaVomiting',
    'AnesthesiaComplications'
  ),
  [handlerKey(
    'AnesthesiaComplications',
    'AnesthesiaComplications.FailedLocalAnesthesia'
  )]: basic(
    'AnesthesiaComplications.FailedLocalAnesthesia',
    'AnesthesiaComplications'
  ),
  [handlerKey('AnesthesiaComplications', 'AnesthesiaComplications.Pain')]:
    basic('AnesthesiaComplications.Pain', 'AnesthesiaComplications'),
  [handlerKey(
    'AnesthesiaComplications',
    'AnesthesiaComplications.InhalationOrRespiratoryInfection'
  )]: basic(
    'AnesthesiaComplications.InhalationOrRespiratoryInfection',
    'AnesthesiaComplications'
  ),
  [handlerKey(
    'AnesthesiaComplications',
    'AnesthesiaComplications.DifficultIntubation'
  )]: basic(
    'AnesthesiaComplications.DifficultIntubation',
    'AnesthesiaComplications'
  ),
  [handlerKey(
    'AnesthesiaComplications',
    'AnesthesiaComplications.DifficultInfusion'
  )]: basic(
    'AnesthesiaComplications.DifficultInfusion',
    'AnesthesiaComplications'
  ),
  [handlerKey('EpiduralHO', 'EpiduralHO.Success')]: basic(
    'EpiduralHO.Success',
    'EpiduralHO'
  ),
  [handlerKey('EpiduralHO', 'EpiduralHO.Difficulty')]: basic(
    'EpiduralHO.Difficulty',
    'EpiduralHO'
  ),
  [handlerKey('EpiduralHO', 'EpiduralHO.Complication')]: basic(
    'EpiduralHO.Complication',
    'EpiduralHO'
  ),
  [handlerKey(
    'HOChronicPainAlgodystrophy',
    'HOChronicPainAlgodystrophy.HOAlgodystrophy'
  )]: basic(
    'HOChronicPainAlgodystrophy.HOAlgodystrophy',
    'HOChronicPainAlgodystrophy'
  ),
  [handlerKey(
    'HOChronicPainAlgodystrophy',
    'HOChronicPainAlgodystrophy.HOChronicPain'
  )]: basic(
    'HOChronicPainAlgodystrophy.HOChronicPain',
    'HOChronicPainAlgodystrophy'
  ),
  [handlerKey(undefined, MedicalScoreName.APFEL)]: score(MedicalScoreName.APFEL)
};

const CARDIO_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey(undefined, '49436004')]: basic(
    'Atrial fibrillation',
    undefined,
    '49436004',
    'I48.91'
  ),
  [handlerKey(undefined, '399957001')]: basic('PAOD', undefined, '399957001'),
  [handlerKey('TwoFlightsStairs', 'YES')]: yesNo('TwoFlightsStairs'),
  [handlerKey('CardiologyHO', 'HighBloodPressure')]: biChoice(
    'CardiologyHO',
    'HighBloodPressure.NO',
    'HighBloodPressure'
  ),
  [handlerKey('CardiologyHO', 'CardiologyHO')]: drawer('CardiologyHO'),
  [handlerKey('HOVenousThrombosis', 'HOVenousThrombosis')]:
    drawer('HOVenousThrombosis'),
  [handlerKey('CardiologyHO', 'HeartFailure')]: biChoice(
    'CardiologyHO',
    'HeartFailure.NO',
    'HeartFailure'
  ),
  [handlerKey('CardiologyHO', 'ChestPainHO')]: biChoice(
    'CardiologyHO',
    'ChestPainHO.NO',
    'ChestPainHO'
  ),
  [handlerKey('CardiacRhythm', 'Pacemaker')]: biChoice(
    'CardiacRhythm',
    'Pacemaker.NO',
    'Pacemaker'
  ),
  [handlerKey('CardiacRhythm', 'ImplantableDefibrillator')]: biChoice(
    'CardiacRhythm',
    'ImplantableDefibrillator.NO',
    'ImplantableDefibrillator'
  ),
  [handlerKey(undefined, MedicalScoreName.LEE)]: score(MedicalScoreName.LEE),
  [handlerKey(undefined, MedicalScoreName.CARDIAC_RISK_FACTOR)]: score(
    MedicalScoreName.CARDIAC_RISK_FACTOR
  )
};

const PULMONARY_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('Smoker', 'Smoker')]: drawer('Smoker'),
  [handlerKey('Asthma', 'Asthma')]: drawer('Asthma'),
  [handlerKey('SleepApnea', 'YES')]: yesNo('SleepApnea'),
  [handlerKey('CpapMachine', 'YES')]: yesNo('CpapMachine'),
  [handlerKey(undefined, MedicalScoreName.STOP_BANG)]: score(
    MedicalScoreName.STOP_BANG
  ),
  [handlerKey(undefined, '13645005')]: basic('COPD', undefined, '13645005'),
  [handlerKey(undefined, '161528002')]: basic(
    'Pneumothorax',
    undefined,
    '161528002'
  )
};

const NEURO_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('StrokeHO', 'StrokeHO')]: drawer('StrokeHO'),
  [handlerKey('EpilepsyHO', 'YES')]: yesNo('EpilepsyHO'),
  [handlerKey('ConsciousnessLossHO', 'YES')]: yesNo('ConsciousnessLossHO'),
  [handlerKey('DepressionHO', 'YES')]: yesNo('DepressionHO'),
  [handlerKey('MigraineHO', 'YES')]: yesNo('MigraineHO'),
  [handlerKey('AlzheimerHO', 'YES')]: yesNo('AlzheimerHO'),
  [handlerKey('ParkinsonHO', 'YES')]: yesNo('ParkinsonHO')
};

const GI_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('GastricReflux', 'GastricReflux.OccasionalReflux')]: biChoice(
    'GastricReflux',
    'GastricReflux.OccasionalReflux.NO',
    'GastricReflux.OccasionalReflux'
  ),
  [handlerKey('GastricReflux', 'GastricReflux.FrequentReflux')]: biChoice(
    'GastricReflux',
    'GastricReflux.FrequentReflux.NO',
    'GastricReflux.FrequentReflux'
  ),
  [handlerKey('StomachProblemHO', 'StomachProblemHO.GastricUlcer')]: biChoice(
    'StomachProblemHO',
    'StomachProblemHO.GastricUlcer.NO',
    'StomachProblemHO.GastricUlcer'
  ),
  [handlerKey('StomachProblemHO', 'StomachProblemHO.HiatalHernia')]: biChoice(
    'StomachProblemHO',
    'StomachProblemHO.HiatalHernia.NO',
    'StomachProblemHO.HiatalHernia'
  ),
  [handlerKey('StomachProblemHO', 'StomachProblemHO.Pyrosis')]: biChoice(
    'StomachProblemHO',
    'StomachProblemHO.Pyrosis.NO',
    'StomachProblemHO.Pyrosis'
  )
};

const RENAL_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('ChronicKidneyFailureHO', 'ChronicKidneyFailureHO.Yes')]:
    biChoice(
      'ChronicKidneyFailureHO',
      'ChronicKidneyFailureHO.No',
      'ChronicKidneyFailureHO.Yes'
    ),
  [handlerKey(
    'ChronicKidneyFailureHO',
    'ChronicKidneyFailureHO.YesWithDialysis'
  )]: basic('ChronicKidneyFailureHO.YesWithDialysis', 'ChronicKidneyFailureHO'),
  [handlerKey(undefined, '45816000')]: basic(
    'Pyelonephritis',
    undefined,
    '45816000'
  ),
  [handlerKey(undefined, '95570007')]: basic(
    'Kidney stone',
    undefined,
    '95570007'
  ),
  [handlerKey(undefined, '266569009')]: basic(
    'Benign prostatic hypertrophy',
    undefined,
    '266569009'
  )
};

const ENDOCRINE_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey(undefined, '44054006')]: basic(
    'Diabetes T2 without insulin',
    undefined,
    '44054006'
  ),
  [handlerKey(undefined, '237599002')]: basic(
    'Diabetes T2 with insulin',
    undefined,
    '237599002'
  ),
  [handlerKey(undefined, '40930008')]: basic(
    'Hypothyroidism',
    undefined,
    '40930008'
  ),
  [handlerKey('AlcoholConsumptionHO', 'AlcoholConsumptionHO.MoreThan2Alcohol')]:
    basic('AlcoholConsumptionHO.MoreThan2Alcohol', 'AlcoholConsumptionHO'),
  [handlerKey('AlcoholConsumptionHO', 'AlcoholConsumptionHO.OnePerDayAlcohol')]:
    basic('AlcoholConsumptionHO.OnePerDayAlcohol', 'AlcoholConsumptionHO'),
  [handlerKey('MetabolicQuestions', 'HepatitisHO')]: basic(
    'HepatitisHO',
    'MetabolicQuestions',
    undefined,
    undefined,
    'medical_synthesis.TRIAGE_HepatitisHO'
  ),
  [handlerKey('MetabolicQuestions', 'LiverCirrhosis')]: basic(
    'LiverCirrhosis',
    'MetabolicQuestions',
    undefined,
    undefined,
    'medical_synthesis.TRIAGE_LiverCirrhosis'
  )
};

const HEMATO_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey(undefined, MedicalScoreName.HEMSTOP)]: score(
    MedicalScoreName.HEMSTOP
  )
};

const ENT_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('FragileTeeth', 'YES')]: yesNo('FragileTeeth'),
  [handlerKey('Braces', 'Braces.FixedBraces')]: biChoice(
    'Braces',
    'NoBraces',
    'Braces.FixedBraces'
  ),
  [handlerKey('JawJointsProblem', 'YES')]: yesNo('JawJointsProblem')
};

const FAMILY_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('CoagulationRiskFamily', 'YES')]: yesNo('CoagulationRiskFamily'),
  [handlerKey('MalignantHyperthermiaFamily', 'YES')]: yesNo(
    'MalignantHyperthermiaFamily'
  ),
  [handlerKey('VascularRiskFamily', 'YES')]: yesNo('VascularRiskFamily')
};

const OBSTETRIC_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('Gravidity', '0')]: numericUniq('Gravidity', '0'),
  [handlerKey('Parity', '0')]: numericUniq('Parity', '0'),
  [handlerKey('Gravidity', '1')]: numericUniq('Gravidity', '1'),
  [handlerKey('Parity', '1')]: numericUniq('Parity', '1'),
  [handlerKey('Gravidity', '2')]: numericUniq('Gravidity', '2'),
  [handlerKey('Parity', '2')]: numericUniq('Parity', '2'),
  [handlerKey('Gravidity', '3')]: numericUniq('Gravidity', '3'),
  [handlerKey('Parity', '3')]: numericUniq('Parity', '3'),
  [handlerKey(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.DeliverySeverePain'
  )]: biChoice(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.DeliverySeverePain.NO',
    'PreviousPregnanciesHO.DeliverySeverePain'
  ),
  [handlerKey(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.EmergencyCsection'
  )]: biChoice(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.EmergencyCsection.NO',
    'PreviousPregnanciesHO.EmergencyCsection'
  ),
  [handlerKey(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.PhlebitisPregnancy'
  )]: biChoice(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.PhlebitisPregnancy.NO',
    'PreviousPregnanciesHO.PhlebitisPregnancy'
  ),
  [handlerKey(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.PostpartumHemorrhage'
  )]: biChoice(
    'PreviousPregnanciesHO',
    'PreviousPregnanciesHO.PostpartumHemorrhage.NO',
    'PreviousPregnanciesHO.PostpartumHemorrhage'
  ),
  [handlerKey('PreviousPregnanciesHO', 'PreviousPregnanciesHO.PreEclampsia')]:
    basic('PreviousPregnanciesHO.PreEclampsia', 'PreviousPregnanciesHO'),
  [handlerKey(undefined, '11687002')]: basic(
    'Gestational diabetes mellitus',
    undefined,
    '11687002'
  )
};

const GENERAL_HANDLERS: Record<string, HandlerFactory> = {
  [handlerKey('CurrentInfections', 'CurrentInfections.HepatitisB')]: biChoice(
    'CurrentInfections',
    'CurrentInfections.HepatitisB.NO',
    'CurrentInfections.HepatitisB'
  ),
  [handlerKey('CurrentInfections', 'CurrentInfections.HepatitisC')]: biChoice(
    'CurrentInfections',
    'CurrentInfections.HepatitisC.NO',
    'CurrentInfections.HepatitisC'
  ),
  [handlerKey('CurrentInfections', 'CurrentInfections.HIV')]: biChoice(
    'CurrentInfections',
    'CurrentInfections.HIV.NO',
    'CurrentInfections.HIV'
  ),
  [handlerKey('MyastheniaHO', 'YES')]: yesNo('MyastheniaHO'),
  [handlerKey('VisionProblems', 'VisionProblems.SevereMyopia')]: biChoice(
    'VisionProblems',
    'VisionProblems.SevereMyopia.NO',
    'VisionProblems.SevereMyopia'
  ),
  [handlerKey('VisionProblems', 'VisionProblems.ContactLens')]: biChoice(
    'VisionProblems',
    'VisionProblems.ContactLens.NO',
    'VisionProblems.ContactLens'
  ),
  [handlerKey('VisionProblems', 'VisionProblems.TreatedGlaucoma')]: biChoice(
    'VisionProblems',
    'VisionProblems.TreatedGlaucoma.NO',
    'VisionProblems.TreatedGlaucoma'
  ),
  [handlerKey(
    'PreviousMedicationIntolerance',
    'PreviousMedicationIntolerance.Codeine'
  )]: basic(
    'PreviousMedicationIntolerance.Codeine',
    'PreviousMedicationIntolerance'
  ),
  [handlerKey(
    'PreviousMedicationIntolerance',
    'PreviousMedicationIntolerance.Morphine'
  )]: basic(
    'PreviousMedicationIntolerance.Morphine',
    'PreviousMedicationIntolerance'
  ),
  [handlerKey(
    'PreviousMedicationIntolerance',
    'PreviousMedicationIntolerance.Tramadol'
  )]: basic(
    'PreviousMedicationIntolerance.Tramadol',
    'PreviousMedicationIntolerance'
  )
};

const HANDLERS_BY_CATEGORY: Partial<
  Record<string, Record<string, HandlerFactory>>
> = {
  [MedicalHoItemCategory.ANESTHESIA]: ANESTHESIA_HANDLERS,
  [MedicalHoItemCategory.CARDIO_VASCULAR]: CARDIO_HANDLERS,
  [MedicalHoItemCategory.PULMONARY]: PULMONARY_HANDLERS,
  [MedicalHoItemCategory.NEURO]: NEURO_HANDLERS,
  [MedicalHoItemCategory.GI]: GI_HANDLERS,
  [MedicalHoItemCategory.RENAL]: RENAL_HANDLERS,
  [MedicalHoItemCategory.ENDOCRINE]: ENDOCRINE_HANDLERS,
  [MedicalHoItemCategory.HEMATO]: HEMATO_HANDLERS,
  [MedicalHoItemCategory.ENT]: ENT_HANDLERS,
  [MedicalHoItemCategory.FAMILY]: FAMILY_HANDLERS,
  [MedicalHoItemCategory.OBSTETRIC]: OBSTETRIC_HANDLERS,
  [MedicalHoItemCategory.GENERAL]: GENERAL_HANDLERS
};

/**
 * Resolves a single config row to its canonical ScratchSuggestion so callers
 * (e.g. the hospital-params config page) can read `.translation` for display
 * without duplicating handler-specific key logic.
 *
 * - Custom rows fall back to `customBasicChip` (or AllergyBasic for ALLERGIES).
 * - SURGICAL rows mirror `buildSurgicalSuggestions` and always use Basic.
 * - ALLERGIES non-custom rows always use AllergyBasic so any substance from
 *   the global catalog resolves, not just the five static handler entries.
 * - All other categories go through their per-category handler registry;
 *   unknown rows return `undefined`.
 */
export function buildScratchSuggestionFromConfig(
  cfg: ConfigSuggestion
): ScratchSuggestion | undefined {
  if (cfg.category === 'ALLERGIES') {
    if (cfg.isCustom || !cfg.code) {
      return new AllergyBasicScratchMhoSuggestion(
        cfg.code ?? cfg.snomedCode ?? cfg.freeTextLabel!,
        cfg.snomedCode,
        undefined,
        cfg.code ? undefined : cfg.freeTextLabel,
        patientPredicate(cfg),
        cfg.defaultEmphasis
      );
    }
    return new AllergyBasicScratchMhoSuggestion(
      cfg.code,
      cfg.snomedCode,
      undefined,
      undefined,
      patientPredicate(cfg),
      cfg.defaultEmphasis
    );
  }
  if (cfg.category === MedicalHoItemCategory.SURGICAL) {
    const codeKey = cfg.code ?? cfg.snomedCode ?? cfg.freeTextLabel!;
    const translationOverride = cfg.code
      ? `medical_synthesis.TRIAGE_${cfg.code}`
      : cfg.freeTextLabel;
    return new BasicScratchMhoSuggestion(
      codeKey,
      cfg.qcode ?? (cfg.code ? 'PreviousProcedures' : undefined),
      cfg.snomedCode,
      undefined,
      translationOverride,
      patientPredicate(cfg),
      cfg.defaultEmphasis
    );
  }
  if (cfg.isCustom) {
    return customBasicChip(cfg);
  }
  const handlers = HANDLERS_BY_CATEGORY[cfg.category];
  return handlers?.[configHandlerKey(cfg)]?.(cfg) ?? customBasicChip(cfg);
}

export function useMhoSuggestionsMap(): MhoSuggestionsPerCategory {
  const { data: mhoConfig } = useGetMhoSuggestionsQuery();
  return useMemo(() => {
    const h = ScratchSuggestionHelper;
    return {
      ALLERGIES: h.buildAllergySuggestions(mhoConfig),
      [MedicalHoItemCategory.SURGICAL]: h.buildSurgicalSuggestions(mhoConfig),
      [MedicalHoItemCategory.ANESTHESIA]:
        h.buildAnesthesiaSuggestions(mhoConfig),
      [MedicalHoItemCategory.CARDIO_VASCULAR]:
        h.buildCardioSuggestions(mhoConfig),
      [MedicalHoItemCategory.PULMONARY]: h.buildPulmonarySuggestions(mhoConfig),
      [MedicalHoItemCategory.NEURO]: h.buildNeuroSuggestions(mhoConfig),
      [MedicalHoItemCategory.GI]: h.buildGiSuggestions(mhoConfig),
      [MedicalHoItemCategory.RENAL]: h.buildRenalSuggestions(mhoConfig),
      [MedicalHoItemCategory.ENDOCRINE]: h.buildEndocrineSuggestions(mhoConfig),
      [MedicalHoItemCategory.HEMATO]: h.buildHematoSuggestions(mhoConfig),
      [MedicalHoItemCategory.ENT]: h.buildEntSuggestions(mhoConfig),
      [MedicalHoItemCategory.FAMILY]: h.buildFamilySuggestions(mhoConfig),
      [MedicalHoItemCategory.OBSTETRIC]:
        h.buildObstetricsSuggestions(mhoConfig),
      [MedicalHoItemCategory.GENERAL]: h.buildGeneralSuggestions(mhoConfig)
    } as MhoSuggestionsPerCategory;
  }, [mhoConfig]);
}
